import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { getBackend as getBackend2 } from "@/lib/backend/Backend2";
import * as t from "@/lib/backend/manufacturing2.generated.types";
import ErrorComponent from "@/components/ErrorComponent";
import Loader from "@/components/Loader";
import { translate } from "@/components/IntlProviderWrapper";
import { niceNumber } from "@/lib/Util";

type ManufacturingKpiKey = keyof Pick<
    t.IGetStatsRes,
    | "plants_cnt"
    | "linegroups_cnt"
    | "lines_cnt"
    | "productions_24h_cnt"
    | "people_cnt"
    | "material_cnt"
    | "processes_cnt"
    | "productions_cnt"
>;

type ManufacturingKpi = {
    key: ManufacturingKpiKey;
    labelId: string;
    link?: string;
};

const MANUFACTURING_KPIS: ManufacturingKpi[] = [
    { key: "plants_cnt", labelId: "common.plants", link: "/digital-twin/resources/plants" },
    { key: "linegroups_cnt", labelId: "common.linegroups", link: "/digital-twin/resources/line_groups" },
    { key: "lines_cnt", labelId: "common.lines", link: "/digital-twin/resources/lines" },
    { key: "productions_24h_cnt", labelId: "common.production", link: "/digital-twin/production-data/productions" },
    { key: "people_cnt", labelId: "common.people", link: "/digital-twin/resources/people" },
    { key: "material_cnt", labelId: "common.materials", link: "/digital-twin/resources/materials" },
    { key: "processes_cnt", labelId: "common.processes", link: "/digital-twin/resources/processes" },
    { key: "productions_cnt", labelId: "common.productions", link: "/digital-twin/production-data/productions" },
];

export default function ManufacturingStats() {
    const [stats, setStats] = useState<t.IGetStatsRes | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        void (async () => {
            try {
                const manStats = await getBackend2().manufacturing.getStats({});
                if (!cancelled) {
                    setStats(manStats);
                }
            } catch {
                if (!cancelled) {
                    setError(translate("common.error"));
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <section>
            <h2 className="content-subtitle">{translate("common.manufacturing", "Manufacturing")}</h2>
            <div className="main-section main-section--rounded">
                {error && <ErrorComponent msg={error} type="error" />}
                {loading && <Loader small={true}></Loader>}
                {stats && (
                    <div className="custom-grid gap-3" role="group">
                        {MANUFACTURING_KPIS.map(kpi => (
                            <div key={kpi.key} className="card">
                                <div className="d-flex flex-column justify-content-between gap-3 height-100">
                                    <div className="card-title card-title-small card-title-light card-title-all-caps">
                                        {translate(kpi.labelId)}
                                    </div>
                                    <div className="card-value card-value--big">
                                        {kpi.link ? (
                                            <Link to={kpi.link} className="dash-link">
                                                {niceNumber(stats[kpi.key])}
                                            </Link>
                                        ) : (
                                            niceNumber(stats[kpi.key])
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}
